import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  styled,
  TextareaAutosize,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
  useTheme
} from '@mui/material';
import {
  AppConfig,
  CaseDetails,
  EconsultManagedSpecialtyProgram,
  EconsultManagedSpecialtyProgramRegion,
  EconsultRestrictionNoteSection,
  EconsultUnavailabilityDate,
  EconsultUser,
  EconsultUtils,
  FhirBinary,
  FhirResourceTypes,
  FileUtils,
  FhirResourceUtils,
  isPathologyReportHeading,
  stripSocialHistoryHeading,
  expandObservationSpacing,
  collapseObservationSpacing,
  FileWithProgress,
  NameUtils,
  NoteActionTypes,
  NoteParticipantStatusReasons,
  NoteStatusCodes,
  NoteTypes,
  ProgramProfile,
  ProgramSpecialty,
  UserDTO,
  UserSource
} from '@oh-vcp/components-common';
import { Referrer } from '@oh-vcp/components-common/src/models/case/CaseDetails';
import {
  hasCaseChanged,
  isForwardingToSameManagedProgram
} from '@oh-vcp/components-common/src/utils/econsultCaseUtils';
import {
  DraftDetailsSection,
  LoadingInProgress,
  OhipValidationService,
  RedirectService,
  RequiredFieldTypography,
  UrlService,
  useNotifierContext
} from '@oh-vcp/components-ui';
import {
  EconsultCasePatientForm,
  EconsultCaseSpecialtySection,
  EconsultCategoryDeleteConfirmation,
  EconsultClinicalReportCategorySection,
  EconsultDelegatorSelector,
  EconsultDeleteDraftDialog,
  EconsultDraftCaseActions,
  EconsultDragAndDrop,
  EconsultOutOfOfficeDialog,
  EconsultPriorityRegionDialog,
  EconsultRestrictionNote,
  EconsultSelectRequesterDialog,
  EconsultSpecialistSearch,
  EconsultUserService,
  fetchRegion,
  fetchSmartOnFhirData,
  NotificationFactory,
  NotificationsConfig,
  apiGetRaw,
  useAuth,
  useConsultantService,
  useFhirContext,
  usePeopleProfileQuery,
  useProcessedPatientFhirData,
  useSmartOnFhirPatientData,
  useSmartOnFhirResource,
  useUpdateSpecialistUser,
  useUserSettings
} from '@oh-vcp/components-web';
import ValidatorUtils from '@oh-vcp/components-web/src/econsultBaseValidator/EconsultBaseValidator';
import { ValidationTypes } from '@oh-vcp/components-web/src/econsultBaseValidator/EconsultValidationTypes';
import { ValidationResult } from '@oh-vcp/components-web/src/econsultBaseValidator/ValidationResult';
import { useEconsultCaseDetailsContext } from '@oh-vcp/components-web/src/econsultCaseDetailsContext/EconsultCaseDetailsContext';
import { EconsultCaseSpecialtySectionRef } from '@oh-vcp/components-web/src/econsultCaseSpecialtySection/EconsultCaseSpecialtySection';
import EconsultDraftLostWarningDialog from '@oh-vcp/components-web/src/econsultDraftLostWarningDialog/EconsultDraftLostWarningDialog';
import { EconsultDragAndDropRef } from '@oh-vcp/components-web/src/econsultDragAndDrop/EconsultDragAndDrop';
import { EconsultSpecialistSearchRef } from '@oh-vcp/components-web/src/econsultSpecialistSearch/EconsultSpecialistSearch';
import { useEconsultTabContext } from '@oh-vcp/components-web/src/econsultTabContext/EconsultTabContext';
import useSafariHitTestFix from '@oh-vcp/components-web/src/hooks/useSafariHitTestFix';
import CaseService from '@oh-vcp/components-web/src/services/caseService';
import useCaseDetails from '@oh-vcp/components-web/src/services/useCaseDetails';
import useManagedSpecialtyProgramLookup from '@oh-vcp/components-web/src/services/useManagedSpecialtyProgramLookup';
import useProgramLookup from '@oh-vcp/components-web/src/services/useProgramLookup';
import useSmartOnFhirPatientDetails from '@oh-vcp/components-web/src/services/useSmartOnFhirPatientDetails';
import { get } from 'http';
import React, { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9'
  }
}));

const StyledTextAreaAutoResize = styled(TextareaAutosize)(({ theme }) => ({
  '&::placeholder': {
    fontSize: '0.875rem',
    color: theme.palette.base.grey5
  },
  color: 'rgba(0, 0, 0, 0.87)',
  '&[aria-hidden="true"]': {
    height: '0 !important',
    minHeight: '0 !important',
    maxHeight: '0 !important',
    overflow: 'hidden !important',
    visibility: 'hidden',
    pointerEvents: 'none'
  }
}));

interface EconsultCaseDraftDetailsViewProps {
  getUpdatedCaseDetails: () => CaseDetails | undefined;
  getPreviousCaseDetails: () => CaseDetails | undefined;
  setPreviousCaseDetails: (previousCaseDetails: CaseDetails) => void;
  setUpdatedCaseDetails: (updCaseDetails: CaseDetails | undefined) => void;
  setDraftCaseChanged: (caseChanged: boolean) => void;
}

interface EconsultCasePatientFormHandle {
  validate: () => boolean;
}

const OBSERVATION_LABORATORY_SECTION = 'Laboratory';
const OBSERVATION_PATHOLOGY_SECTION = 'Pathology';
const OBSERVATION_LABORATORY_HEADINGS = ['Laboratory/Pathology', OBSERVATION_LABORATORY_SECTION];
const OBSERVATION_PATHOLOGY_HEADINGS = [OBSERVATION_PATHOLOGY_SECTION];

type ObservationParts = {
  laboratory: string;
  laboratoryHeading: string;
  pathology: string;
  other: string;
};

type ClinicalReportSection = {
  key: string;
  labelKey: string;
  content: string;
  onContentChanged: (value: string) => void;
  readOnly?: boolean;
  isLabResult?: boolean;
  isPathologyReport?: boolean;
  isDiagnosticReport?: boolean;
  isEncounter?: boolean;
};

const toObservationBlocks = (content: string): string[] =>
  content ? content.split('\n\n').filter((block) => block.trim()) : [];

const observationBlockHeading = (block: string) => block.split('\n')[0].trim();

const observationBlockBody = (block: string) => block.split('\n').slice(1).join('\n').trim();

const isObservationPathologyBlock = (block: string) => {
  const heading = observationBlockHeading(block);
  return OBSERVATION_PATHOLOGY_HEADINGS.includes(heading) || isPathologyReportHeading(heading);
};

const splitObservationContent = (content: string): ObservationParts => {
  const blocks = toObservationBlocks(content);
  const headings = blocks.map(observationBlockHeading);

  const bodiesUnder = (sectionHeadings: string[]) =>
    blocks
      .filter((block) => sectionHeadings.includes(observationBlockHeading(block)))
      .map(observationBlockBody)
      .filter(Boolean)
      .join('\n');

  return {
    laboratory: bodiesUnder(OBSERVATION_LABORATORY_HEADINGS),
    laboratoryHeading:
      headings.find((heading) => OBSERVATION_LABORATORY_HEADINGS.includes(heading)) ??
      OBSERVATION_LABORATORY_SECTION,
    pathology: blocks.filter(isObservationPathologyBlock).join('\n\n'),
    other: blocks
      .filter(
        (block) =>
          !OBSERVATION_LABORATORY_HEADINGS.includes(observationBlockHeading(block)) &&
          !isObservationPathologyBlock(block)
      )
      .join('\n\n')
  };
};

const joinObservationContent = (parts: ObservationParts): string =>
  [
    parts.laboratory.trim() ? `${parts.laboratoryHeading}\n${parts.laboratory.trim()}` : '',
    parts.pathology.trim(),
    parts.other.trim()
  ]
    .filter(Boolean)
    .join('\n\n');

const EconsultCaseDraftDetailsView: FC<EconsultCaseDraftDetailsViewProps> = ({
  getUpdatedCaseDetails,
  getPreviousCaseDetails,
  setPreviousCaseDetails,
  setUpdatedCaseDetails,
  setDraftCaseChanged
}) => {
  const {
    caseDetails,
    setCaseDetails,
    abortAllUploads,
    draftCaseSaveDisable,
    setDraftCaseSaved,
    setDraftCaseSaveDisable,
    draftCaseSaved,
    fhirPatientGuid,
    fhirContext,
    isSubmitting,
    autoSaveInProgress
  } = useEconsultCaseDetailsContext();
  const [isRecipientError, setIsRecipientError] = useState<boolean>(false);
  const [recipientSelected, setRecipientSelected] = useState<boolean>(false);
  const [disableActionButtons, setDisableActionButtons] = useState<boolean>(false);
  const { addNotification, removeAllErrorNotifications } = useNotifierContext();
  const { data: consultantsData } = useConsultantService();
  const [isDeleteDraftDialogOpen, setIsDeleteDraftDialogOpen] = useState<boolean>(false);
  const [regionList, setRegionList] = useState<EconsultManagedSpecialtyProgramRegion[] | undefined>(
    undefined
  );
  const [selectedRegionId, setSelectedRegionId] = useState<number | undefined>(undefined);
  const [selectedProgramId, setSelectedProgamId] = useState<number | undefined>(undefined);
  const [specialtyRestriction, setSpecialtyRestrictions] = useState<
    EconsultRestrictionNoteSection[] | []
  >([]);
  const [programRestrictions, setProgramRestrictions] = useState<
    EconsultRestrictionNoteSection[] | []
  >([]);
  const [isRegionSelectShown, setIsRegionSelectShown] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(true);
  const [isPriorityRegionDialogOpen, setIsPriorityRegionDialogOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<(UserDTO | EconsultManagedSpecialtyProgram)[]>([]);
  const [myConsultants, setMyConsultants] = useState<EconsultUser[]>([]);
  const [draftFiles, setDraftFiles] = useState<FileWithProgress[]>([]);
  const [outOfOfficeConsultant, setOutOfOfficeConsultant] = useState<UserDTO | undefined>(
    undefined
  );
  const [allergyContent, setAllergyContent] = useState<string>('');
  const [conditionContent, setConditionContent] = useState<string>('');
  const [medicationContent, setMedicationContent] = useState<string>('');
  const [historyContent, setHistoryContent] = useState<string>('');
  const [observationContent, setObservationContent] = useState<string>('');
  const [immunizationContent, setImmunizationContent] = useState<string>('');
  const [procedureContent, setProcedureContent] = useState<string>('');
  const [diagnosticReportContent, setDiagnosticReportContent] = useState<string>('');
  const [encounterContent, setEncounterContent] = useState<string>('');
  const [consultOutOfOfficeDate, setConsultOutOfOfficeDate] = useState<
    EconsultUnavailabilityDate | null | undefined
  >(undefined);
  const [acknowledgedOutOfOffice, setAcknowledgedOutOfOffice] = useState<boolean>(false);
  const [isSelectRequesterDialogOpen, setIsSelectRequesterDialogOpen] = useState<boolean>(false);
  const [prioritySpecialty, setPrioritySpecialty] = useState<ProgramSpecialty | undefined>(
    undefined
  );
  const [prioritySubSpecialty, setPrioritySubSpecialty] = useState<ProgramSpecialty | undefined>(
    undefined
  );
  const theme = useTheme();
  const { t } = useTranslation();
  const { caseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    data: managedPrograms,
    isFetching: isManagedSpecialtyFetching,
    isError: isManagedSpecialtyError
  } = useManagedSpecialtyProgramLookup(
    caseDetails?.referrer?.userId,
    caseDetails?.specialty?.code,
    caseDetails?.subSpecialty?.subCode
  );
  const {
    data: managedProgram,
    isFetching: isProgramLookupFetching,
    isError: isProgramLookupError
  } = useProgramLookup(selectedProgramId);

  const initialProgramId = useRef(caseDetails?.participant?.program?.programId);
  const { data: participantProgram } = useProgramLookup(initialProgramId.current);
  const { data: originalCaseDetails } = useCaseDetails(caseDetails?.originalCaseId);
  const { setSelectedTab } = useEconsultTabContext();
  const specialistClearRef = useRef<EconsultSpecialistSearchRef>(null);
  const attachFileRef = useRef<EconsultDragAndDropRef>(null);
  const specialityRef = useRef<EconsultCaseSpecialtySectionRef>(null);
  const editedClinicalReportTypesRef = useRef<Set<string>>(new Set());
  const hydratedExternalReferralContentRef = useRef<string | undefined>(undefined);
  const queryClient = useQueryClient();
  const { data: settings } = useUserSettings(
    caseDetails?.participant?.consultant?.userId,
    'econsult'
  );
  const [searchParams] = useSearchParams();
  const selectedRegionName = useMemo(() => {
    if (selectedRegionId) {
      return regionList?.find((r) => r.id === selectedRegionId)?.name || '';
    }
    return regionList?.[0]?.name;
  }, [regionList, selectedRegionId]);
  const specialistId = UrlService.getParamIgnoreCase(searchParams, 'specialistId');
  const { data: consultantProfile } = usePeopleProfileQuery(
    caseDetails?.participant?.consultant?.contactId?.toString()
  );
  const { data: smartOnFhirPatientAllergyDetails, isLoading: isAllergyLoading } =
    useSmartOnFhirPatientData(FhirResourceTypes.AllergyIntolerance, fhirPatientGuid, !fhirContext);
  const { data: smartOnFhirPatientConditionDetails, isLoading: isConditionLoading } =
    useSmartOnFhirPatientData(FhirResourceTypes.Condition, fhirPatientGuid, !fhirContext);
  const { data: smartOnFhirPatientMedicationDetails, isLoading: isMedicationLoading } =
    useSmartOnFhirPatientData(FhirResourceTypes.MedicationRequest, fhirPatientGuid, !fhirContext);

  const { data: smartOnFhirPatientHistoryDetails, isLoading: isHistoryLoading } =
    useSmartOnFhirPatientData(FhirResourceTypes.FamilyMemberHistory, fhirPatientGuid, !fhirContext);

  const { data: smartOnFhirPatientObservationDetails, isLoading: isObservationLoading } =
    useSmartOnFhirPatientData(FhirResourceTypes.Observation, fhirPatientGuid, !fhirContext);
  const { data: smartOnFhirPatientImmunizationDetails, isLoading: isImmunizationLoading } =
    useSmartOnFhirPatientData(FhirResourceTypes.Immunization, fhirPatientGuid, !fhirContext);
  const { data: smartOnFhirPatientProcedureDetails, isLoading: isProcedureLoading } =
    useSmartOnFhirPatientData(FhirResourceTypes.Procedure, fhirPatientGuid, !fhirContext);
  const { data: smartOnFhirPatientDiagnosticReportDetails, isLoading: isDiagnosticReportLoading } =
    useSmartOnFhirPatientData(FhirResourceTypes.DiagnosticReport, fhirPatientGuid, !fhirContext);
  const { data: smartOnFhirPatientEncounterDetails, isLoading: isEncounterLoading } =
    useSmartOnFhirPatientData(FhirResourceTypes.Encounter, fhirPatientGuid, !fhirContext);
  const {
    data: smartOnFhirPatientMedicationStatementDetails,
    isLoading: isMedicationStatementLoading
  } = useSmartOnFhirPatientData(
    FhirResourceTypes.MedicationStatement,
    fhirPatientGuid,
    !fhirContext
  );
  const isLoadingSmartOnFhirData = [
    isAllergyLoading,
    isConditionLoading,
    isMedicationLoading,
    isHistoryLoading,
    isObservationLoading,
    isImmunizationLoading,
    isProcedureLoading,
    isDiagnosticReportLoading,
    isEncounterLoading,
    isMedicationStatementLoading
  ].some(Boolean);
  const hasInitialized = useRef(false);
  const {
    resourceMap: fhirContextData,
    supportingInfoTypes,
    isLoading: isFhirContextLoading
  } = useFhirContext(fhirContext);
  const { data: smartOnFhirPatientDetails } = useSmartOnFhirPatientDetails(fhirPatientGuid);
  useUpdateSpecialistUser(specialistId, caseDetails, setCaseDetails, specialistClearRef);

  const docRefArray = useMemo(() => {
    const docRefs = fhirContextData?.[FhirResourceTypes.DocumentReference];
    if (!docRefs) return [];
    return Array.isArray(docRefs) ? docRefs : [docRefs];
  }, [fhirContextData]);

  /* validation configuration */

  let [dirtyFields, setFieldDiryty] = useState<string[]>([]);
  let [invalidFields, setFieldInvalid] = useState<string[]>([]);
  const requiredFields: Map<string, ValidationTypes> = new Map();
  requiredFields.set('requestNte', ValidationTypes.Text);

  const validFieldMessageType = {
    display: 'none'
  };
  const inVaildFieldMessageType = {
    display: 'block',
    color: 'red',
    fontSize: '0.75rem'
  };
  const nonUrgentMessageHide = {
    display: 'none',
    color: '',
    fontSize: ''
  };
  const nonUrgentMessageShow = {
    display: 'block',
    color: 'grey',
    fontSize: '0.75rem'
  };
  const patientFormRef = useRef<EconsultCasePatientFormHandle>();
  const patientFormDetailsSectionRef = useRef<HTMLDivElement | null>(null);
  const requestNoteSectionRef = useRef<HTMLTextAreaElement | null>(null);
  const [requestNteErrorMessageStyle, setRequestNteErrorMessageStyle] =
    useState(validFieldMessageType);
  const [programTypeErrorMessageStyle, setProgramTypeErrorMessageStyle] =
    useState(validFieldMessageType);
  const [nonUrgentMessage, setNonUrgentMessage] = useState(nonUrgentMessageShow);

  /* end of validation configuration */
  const unSelectedOptFontColor = 'base.grey4';
  const selectedOptFontColor = 'primary.main';
  const [managedSpecialtyFontColor, setManagedSpecialtyFontColor] =
    useState(unSelectedOptFontColor);
  const [specificProviderFontColor, seSpecificProviderFontColor] = useState(unSelectedOptFontColor);
  const [isSending, setIsSending] = useState<boolean>(false);
  const attachmentSectionRef = useRef<HTMLDivElement | null>(null);
  const formRef = useRef<HTMLDivElement | null>(null);
  useSafariHitTestFix(formRef);
  const [isDraftWarningDialogOpen, setIsDraftWarningDialogOpen] = useState<boolean>(false);
  const [isDeleteCategorySectionDialogOpen, setIsDeleteCategorySectionDialogOpen] =
    useState<boolean>(false);
  const [, setSelectedClinicalReports] = useState<string[]>([]);
  const [clinicReportToDelete, setClinicReportToDelete] = useState<string | null>(null);
  const [resourceBinaryItems, setResourceBinaryItems] = useState<FhirBinary[]>([]);
  const uploadedIdsRef = useRef<Set<string>>(new Set());
  const binaryFileNameByIdRef = useRef<Record<string, string>>({});
  const [binaryFileNamesVersion, setBinaryFileNamesVersion] = useState(0);
  const [deletedReportTypes, setDeletedReportTypes] = useState<Set<string>>(new Set());
  const notifiedOversizedDocIdsRef = useRef<Set<string>>(new Set());

  const getEffectiveContent = (type: string, content: string) => {
    return deletedReportTypes.has(type) ? '' : content;
  };

  const isUserDTO = (obj: unknown): obj is UserDTO => {
    return typeof obj === 'object' && obj !== null && ('userid' in obj || 'userId' in obj);
  };
  const isEconsultManagedSpecialtyProgram = (
    obj: unknown
  ): obj is EconsultManagedSpecialtyProgram => {
    return typeof obj === 'object' && obj !== null && 'programId' in obj && !('userId' in obj);
  };
  const handleBeforeUnload = (event: any) => {
    if (window.location.toString().indexOf('logout') > -1) {
      return;
    }
    event.preventDefault();
    event.returnValue = ''; // Required for Chrome and Firefox
    return ''; // Required for Safari
  };
  const handlePopState = (event: PopStateEvent) => {
    console.log(`popstate: ${event}`);
    const updCaseDetails = getUpdatedCaseDetails();
    if (
      updCaseDetails &&
      updCaseDetails.statusCd &&
      updCaseDetails.statusCd === 'DRAFT' &&
      hasCaseChanged(updCaseDetails, getPreviousCaseDetails())
    ) {
      setIsDraftWarningDialogOpen(true);
    }
  };
  const hasReceipient = () => {
    if (caseDetails?.participant?.consultant && caseDetails?.participant?.consultant?.userId) {
      return true;
    } else if (caseDetails?.participant?.program && caseDetails?.participant?.program?.programId) {
      return true;
    }
    return false;
  };

  const externalReferralContent = useMemo(() => {
    const content = {
      [FhirResourceTypes.AllergyIntolerance]: getEffectiveContent(
        FhirResourceTypes.AllergyIntolerance,
        allergyContent
      ),
      [FhirResourceTypes.Condition]: getEffectiveContent(
        FhirResourceTypes.Condition,
        conditionContent
      ),
      [FhirResourceTypes.DiagnosticReport]: getEffectiveContent(
        FhirResourceTypes.DiagnosticReport,
        diagnosticReportContent
      ),
      [FhirResourceTypes.Encounter]: getEffectiveContent(
        FhirResourceTypes.Encounter,
        encounterContent
      ),
      [FhirResourceTypes.FamilyMemberHistory]: getEffectiveContent(
        FhirResourceTypes.FamilyMemberHistory,
        historyContent
      ),
      [FhirResourceTypes.Immunization]: getEffectiveContent(
        FhirResourceTypes.Immunization,
        immunizationContent
      ),
      [FhirResourceTypes.MedicationRequest]: getEffectiveContent(
        FhirResourceTypes.MedicationRequest,
        medicationContent
      ),
      [FhirResourceTypes.Observation]: getEffectiveContent(
        FhirResourceTypes.Observation,
        observationContent
      ),
      [FhirResourceTypes.Procedure]: getEffectiveContent(
        FhirResourceTypes.Procedure,
        procedureContent
      )
    };

    const filtered = Object.fromEntries(
      Object.entries(content).filter(([, value]) => {
        return value !== '';
      })
    );

    return JSON.stringify(filtered);
  }, [
    allergyContent,
    conditionContent,
    diagnosticReportContent,
    encounterContent,
    historyContent,
    immunizationContent,
    medicationContent,
    observationContent,
    procedureContent,
    deletedReportTypes
  ]);

  const hasExternalReferralContent = !!externalReferralContent && externalReferralContent !== '{}';

  const hasCaseBeenAutoReturnedBySystem = useCallback(() => {
    if (!originalCaseDetails || !originalCaseDetails.data || !originalCaseDetails.data.notes) {
      return false;
    }
    var systemNote = originalCaseDetails.data.notes.find((n) => n.createdBy === NoteTypes.system);

    if (
      systemNote &&
      systemNote.createdBy === NoteTypes.system &&
      systemNote.lastUpdatedBy === NoteTypes.system &&
      systemNote.noteTypeCd === NoteTypes.consultant &&
      (systemNote.actionTypeCd === NoteActionTypes.decline ||
        systemNote.actionTypeCd === NoteActionTypes.unassign) &&
      originalCaseDetails.data.participant?.statusCd === NoteStatusCodes.closed &&
      originalCaseDetails.data.participant?.statusReason ===
        NoteParticipantStatusReasons.consultantUnavailable
    ) {
      return true;
    } else {
      return false;
    }
  }, [originalCaseDetails]);

  const isOriginalRecipientDifferent = useCallback(() => {
    if (hasCaseBeenAutoReturnedBySystem()) {
      return true;
    }
    if (
      originalCaseDetails &&
      originalCaseDetails.data &&
      originalCaseDetails.data.programType === 'MANAGED_SPECIALTY_GROUP' &&
      originalCaseDetails.data.specialty?.code === caseDetails?.specialty?.code &&
      originalCaseDetails.data.subSpecialty?.subCode === caseDetails?.subSpecialty?.subCode &&
      originalCaseDetails.data.participant?.program?.programId === selectedProgramId
    ) {
      return false;
    }
    return true;
  }, [
    hasCaseBeenAutoReturnedBySystem,
    originalCaseDetails,
    caseDetails?.specialty?.code,
    caseDetails?.subSpecialty?.subCode,
    selectedProgramId
  ]);

  const maxDocRefSizeInBytes = 500 * 1024 * 1024;

  const validDocRefArray = useMemo(
    () =>
      docRefArray.filter((doc) => {
        const size = (doc as any)?.content?.[0]?.attachment?.size;
        return size === undefined || size <= maxDocRefSizeInBytes;
      }),
    [docRefArray]
  );

  const oversizedDocRefArray = useMemo(
    () =>
      docRefArray.filter((doc) => {
        const size = (doc as any)?.content?.[0]?.attachment?.size;
        return size !== undefined && size > maxDocRefSizeInBytes;
      }),
    [docRefArray]
  );

  useEffect(() => {
    if (oversizedDocRefArray.length === 0) return;
    oversizedDocRefArray.forEach((doc) => {
      const id = (doc as any)?.id as string;
      if (!id || notifiedOversizedDocIdsRef.current.has(id)) return;
      const fallbackName =
        ((doc as any)?.content?.[0]?.attachment?.title as string | undefined) || id;
      addNotification({
        type: 'error',
        message: `${t('Case.file.unsupportedMessage3')} ${fallbackName} ${t(
          'Case.file.unsupportedMessage4'
        )}`,
        id: parseInt(id, 10) || undefined
      });
      notifiedOversizedDocIdsRef.current.add(id);
    });
  }, [oversizedDocRefArray]);

  const docRefIds = useMemo(
    () => new Set(docRefArray.map((doc) => doc?.id as string).filter(Boolean)),
    [docRefArray]
  );

  const docRefFallbackFileNameById = useMemo(() => {
    const fallbackMap: Record<string, string> = {};

    docRefArray.forEach((doc) => {
      const id = doc?.id as string;
      const title = doc?.content?.[0]?.attachment?.title as string | undefined;

      if (id && title && title.trim()) {
        fallbackMap[id] = title.trim();
      }
    });

    return fallbackMap;
  }, [docRefArray]);

  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }

    return btoa(binary);
  };

  const parseFilenameFromContentDisposition = (
    contentDisposition: string | undefined
  ): string | undefined => {
    if (!contentDisposition) {
      return undefined;
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1]).replace(/^"|"$/g, '').trim();
      } catch {
        return utf8Match[1].replace(/^"|"$/g, '').trim();
      }
    }

    const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return filenameMatch?.[1]?.trim();
  };

  // One query per DocRef binary — stable per-ID key prevents re-fetching already-loaded
  // binaries when new DocRefs are added to validDocRefArray.
  const docRefBinaryResults = useQueries(
    validDocRefArray.map((doc) => {
      const id = doc?.id as string;
      const contentType = doc?.content?.[0]?.attachment?.contentType as string | undefined;
      return {
        queryKey: ['documentReferenceBinary', id],
        queryFn: async (): Promise<FhirBinary | null> => {
          if (!id) return null;
          try {
            const result = await apiGetRaw(
              `${AppConfig.baseSofLaunchUrl}/fhir/DocumentReference/${id}/$binary`,
              undefined,
              {
                headers: { Accept: contentType || 'application/octet-stream' },
                responseType: 'arraybuffer'
              }
            );
            const responseData = (result as any)?.data;
            const responseHeaders = (result as any)?.headers;
            const normalizedHeaders =
              responseHeaders && typeof responseHeaders.toJSON === 'function'
                ? responseHeaders.toJSON()
                : responseHeaders;
            const contentDisposition =
              (typeof responseHeaders?.get === 'function'
                ? responseHeaders.get('content-disposition') ||
                  responseHeaders.get('Content-Disposition')
                : undefined) ||
              normalizedHeaders?.['content-disposition'] ||
              normalizedHeaders?.['Content-Disposition'];
            const responseContentType =
              (typeof responseHeaders?.get === 'function'
                ? responseHeaders.get('content-type') || responseHeaders.get('Content-Type')
                : undefined) ||
              normalizedHeaders?.['content-type'] ||
              normalizedHeaders?.['Content-Type'];
            const resolvedContentType =
              contentType ||
              responseContentType?.split(';')[0]?.trim() ||
              'application/octet-stream';
            const fileName =
              parseFilenameFromContentDisposition(contentDisposition) ||
              docRefFallbackFileNameById[id];
            const base64 =
              responseData instanceof ArrayBuffer
                ? arrayBufferToBase64(responseData)
                : typeof responseData === 'string'
                ? responseData
                : undefined;
            if (!base64) return null;
            if (fileName && binaryFileNameByIdRef.current[id] !== fileName) {
              binaryFileNameByIdRef.current[id] = fileName;
              setBinaryFileNamesVersion((prev) => prev + 1);
            }
            return {
              contentType: resolvedContentType,
              data: base64,
              id,
              resourceType: 'Binary' as const
            };
          } catch (e) {
            console.error(`Error fetching DocumentReference binary ${id}:`, e);
            return null;
          }
        },
        enabled: !!id,
        staleTime: Infinity,
        retry: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        onSuccess: (binary: FhirBinary | null) => {
          if (!binary) return;
          setResourceBinaryItems((prev) => {
            const index = prev.findIndex((item) => item.id === binary.id);
            if (index === -1) return [...prev, binary];
            if (!prev[index].data && binary.data) {
              const merged = [...prev];
              merged[index] = { ...merged[index], ...binary };
              return merged;
            }
            return prev;
          });
        }
      };
    })
  );

  const isDocRefBinaryFetchInProgress = docRefBinaryResults.some((r) => r.isFetching);

  useEffect(() => {
    if ((fhirContext || fhirPatientGuid) && caseDetails && !caseDetails?.notes?.[0]?.noteContent) {
      const contextNote = t('Case.draft.contextNote') as string;
      const updatedCaseDetails = {
        ...caseDetails,
        notes: [
          ...(caseDetails.notes
            ? [
                {
                  ...caseDetails.notes[0],
                  noteContent: contextNote
                }
              ]
            : []),
          ...(caseDetails.notes ? caseDetails.notes.slice(1) : [])
        ]
      };
      setCaseDetails(updatedCaseDetails);
    }
  }, [fhirContext, fhirPatientGuid, caseDetails?.notes]);

  useEffect(() => {
    if (!isLoadingSmartOnFhirData && (fhirContext || fhirPatientGuid) && !hasInitialized.current) {
      hasInitialized.current = true;
      handleDraftSaved(true);
    }
  }, [isLoadingSmartOnFhirData, fhirContext, fhirPatientGuid]);

  useEffect(() => {
    editedClinicalReportTypesRef.current.clear();
    hydratedExternalReferralContentRef.current = undefined;
  }, [caseDetails?.caseId]);

  useEffect(() => {
    if (
      EconsultUtils.isSmartOnFhiUser(user?.source) &&
      myConsultants &&
      myConsultants.length > 1 &&
      (!caseDetails?.referrer?.userId || caseDetails?.referrer?.userId === user?.userid)
    ) {
      setIsSelectRequesterDialogOpen(true);
    }
  }, [user?.source, myConsultants, caseDetails?.referrer?.userId]);

  const createHydrationGuardedSetter = (
    type: string,
    setter: React.Dispatch<React.SetStateAction<string>>
  ): React.Dispatch<React.SetStateAction<string>> => {
    return (value) => {
      if (editedClinicalReportTypesRef.current.has(type)) {
        return;
      }

      setter(value);
    };
  };

  const createHydrationGuardedReportsSetter = (
    type: string
  ): React.Dispatch<React.SetStateAction<string[]>> => {
    return (value) => {
      if (editedClinicalReportTypesRef.current.has(type)) {
        return;
      }
      setSelectedClinicalReports(value);
    };
  };

  const patientPathologyObservationRefs = useMemo(
    () =>
      FhirResourceUtils.collectPathologyObservationRefs(
        (smartOnFhirPatientDiagnosticReportDetails?.entry ?? []).map(
          (entry: any) => entry?.resource
        )
      ),
    [smartOnFhirPatientDiagnosticReportDetails]
  );

  const patientObservationContext = useMemo(
    () => ({ pathologyObservationRefs: patientPathologyObservationRefs }),
    [patientPathologyObservationRefs]
  );

  useProcessedPatientFhirData(
    [
      {
        data: smartOnFhirPatientAllergyDetails,
        setter: createHydrationGuardedSetter(
          FhirResourceTypes.AllergyIntolerance,
          setAllergyContent
        ),
        type: FhirResourceTypes.AllergyIntolerance,
        setSelectedReports: createHydrationGuardedReportsSetter(
          FhirResourceTypes.AllergyIntolerance
        ),
        setBinaryItems: setResourceBinaryItems
      },
      {
        data: smartOnFhirPatientConditionDetails,
        setter: createHydrationGuardedSetter(FhirResourceTypes.Condition, setConditionContent),
        type: FhirResourceTypes.Condition,
        setSelectedReports: createHydrationGuardedReportsSetter(FhirResourceTypes.Condition),
        setBinaryItems: setResourceBinaryItems
      },
      {
        data: smartOnFhirPatientMedicationDetails,
        setter: createHydrationGuardedSetter(
          FhirResourceTypes.MedicationRequest,
          setMedicationContent
        ),
        type: FhirResourceTypes.MedicationRequest,
        setSelectedReports: createHydrationGuardedReportsSetter(
          FhirResourceTypes.MedicationRequest
        ),
        setBinaryItems: setResourceBinaryItems
      },
      {
        data: smartOnFhirPatientMedicationStatementDetails,
        setter: createHydrationGuardedSetter(
          FhirResourceTypes.MedicationRequest,
          setMedicationContent
        ),
        type: FhirResourceTypes.MedicationStatement,
        setSelectedReports: createHydrationGuardedReportsSetter(
          FhirResourceTypes.MedicationRequest
        ),
        setBinaryItems: setResourceBinaryItems
      },
      {
        data: smartOnFhirPatientHistoryDetails,
        setter: createHydrationGuardedSetter(
          FhirResourceTypes.FamilyMemberHistory,
          setHistoryContent
        ),
        type: FhirResourceTypes.FamilyMemberHistory,
        setSelectedReports: createHydrationGuardedReportsSetter(
          FhirResourceTypes.FamilyMemberHistory
        ),
        setBinaryItems: setResourceBinaryItems
      },
      {
        data: smartOnFhirPatientObservationDetails,
        setter: createHydrationGuardedSetter(FhirResourceTypes.Observation, setObservationContent),
        type: FhirResourceTypes.Observation,
        setSelectedReports: createHydrationGuardedReportsSetter(FhirResourceTypes.Observation),
        setBinaryItems: setResourceBinaryItems,
        context: patientObservationContext,
        enabled: !isDiagnosticReportLoading
      },
      {
        data: smartOnFhirPatientImmunizationDetails,
        setter: createHydrationGuardedSetter(
          FhirResourceTypes.Immunization,
          setImmunizationContent
        ),
        type: FhirResourceTypes.Immunization,
        setSelectedReports: createHydrationGuardedReportsSetter(FhirResourceTypes.Immunization),
        setBinaryItems: setResourceBinaryItems
      },
      {
        data: smartOnFhirPatientProcedureDetails,
        setter: createHydrationGuardedSetter(FhirResourceTypes.Procedure, setProcedureContent),
        type: FhirResourceTypes.Procedure,
        setSelectedReports: createHydrationGuardedReportsSetter(FhirResourceTypes.Procedure),
        setBinaryItems: setResourceBinaryItems
      },
      {
        data: smartOnFhirPatientObservationDetails,
        setter: createHydrationGuardedSetter(
          FhirResourceTypes.DiagnosticReport,
          setDiagnosticReportContent
        ),
        type: FhirResourceTypes.DiagnosticReport,
        setSelectedReports: createHydrationGuardedReportsSetter(FhirResourceTypes.DiagnosticReport),
        setBinaryItems: setResourceBinaryItems
      },
      {
        data: smartOnFhirPatientEncounterDetails,
        setter: createHydrationGuardedSetter(FhirResourceTypes.Encounter, setEncounterContent),
        type: FhirResourceTypes.Encounter,
        setSelectedReports: createHydrationGuardedReportsSetter(FhirResourceTypes.Encounter),
        setBinaryItems: setResourceBinaryItems
      }
    ],
    !!fhirPatientGuid
  );

  const getResourceSetter = (
    type: string
  ): React.Dispatch<React.SetStateAction<string>> | undefined => {
    switch (type) {
      case FhirResourceTypes.AllergyIntolerance:
        return setAllergyContent;
      case FhirResourceTypes.Condition:
        return setConditionContent;
      case FhirResourceTypes.DiagnosticReport:
        return setDiagnosticReportContent;
      case FhirResourceTypes.Encounter:
      case FhirResourceTypes.Composition:
        return setEncounterContent;
      case FhirResourceTypes.FamilyMemberHistory:
        return setHistoryContent;
      case FhirResourceTypes.Immunization:
        return setImmunizationContent;
      case FhirResourceTypes.MedicationRequest:
      case FhirResourceTypes.MedicationStatement:
        return setMedicationContent;
      case FhirResourceTypes.Observation:
        return setObservationContent;
      case FhirResourceTypes.Procedure:
        return setProcedureContent;
    }
  };

  const handleClinicalReportContentChanged = (type: string, content: string) => {
    editedClinicalReportTypesRef.current.add(type);
    const setter = getResourceSetter(type);
    if (setter) {
      setter(content);
    }
  };

  useEffect(() => {
    const referralContent = caseDetails?.externalReferralContent;

    if (!referralContent || hydratedExternalReferralContentRef.current === referralContent) {
      return;
    }

    const content = JSON.parse(referralContent);
    if (content) {
      const resources = Object.keys(content);
      resources.forEach((r) => {
        if (!editedClinicalReportTypesRef.current.has(r)) {
          const setter = getResourceSetter(r);
          const contentData = content[r];
          if (setter && typeof contentData === 'string' && contentData.trim().length > 0) {
            setter(contentData);
          }
          if (contentData) {
            setSelectedClinicalReports((prev) => (prev.includes(r) ? prev : [...prev, r]));
          }
        }
      });
    }

    hydratedExternalReferralContentRef.current = referralContent;
  }, [caseDetails?.externalReferralContent]);

  useEffect(() => {
    if (isFhirContextLoading) return;
    if (!fhirContextData || Object.keys(fhirContextData).length === 0) return;

    const diagnosticReports = fhirContextData[FhirResourceTypes.DiagnosticReport];
    const compositions = fhirContextData[FhirResourceTypes.Composition];
    const extractionContext = {
      pathologyObservationRefs: FhirResourceUtils.collectPathologyObservationRefs(
        Array.isArray(diagnosticReports) ? diagnosticReports : [diagnosticReports]
      ),
      composedEncounterRefs: FhirResourceUtils.collectComposedEncounterRefs(
        Array.isArray(compositions) ? compositions : [compositions]
      ),
      supportingInfoTypes
    };

    Object.entries(fhirContextData).forEach(([resourceType, data]: [string, any]) => {
      if (resourceType === FhirResourceTypes.Binary) {
        if (!data) return;
        const binaryArray = Array.isArray(data) ? data : [data];
        setResourceBinaryItems((prev) => {
          const existingIds = new Set(prev.map((item) => item.id).filter(Boolean));
          const newItems = (binaryArray as FhirBinary[]).filter(
            (item) => item.id && !existingIds.has(item.id)
          );
          return newItems.length > 0 ? [...prev, ...newItems] : prev;
        });
      } else {
        const setter = getResourceSetter(resourceType);
        if (!setter || editedClinicalReportTypesRef.current.has(resourceType)) return;
        const resources = Array.isArray(data) ? data : [data];
        const isRadiologyObservation = (resource: any) =>
          resourceType === FhirResourceTypes.Observation && /^rad-\d+$/i.test(resource?.id ?? '');
        const radiologyObservations = resources.filter(isRadiologyObservation);
        const remainingResources = resources.filter(
          (resource) => !isRadiologyObservation(resource)
        );

        if (radiologyObservations.length > 0) {
          const diagnosticReportSetter = getResourceSetter(FhirResourceTypes.DiagnosticReport);
          if (
            diagnosticReportSetter &&
            !editedClinicalReportTypesRef.current.has(FhirResourceTypes.DiagnosticReport)
          ) {
            FhirResourceUtils.processFhirBundleResource(
              { entry: radiologyObservations.map((resource) => ({ resource })) },
              diagnosticReportSetter,
              FhirResourceTypes.DiagnosticReport,
              setSelectedClinicalReports,
              setResourceBinaryItems
            );
          }
        }

        if (remainingResources.length === 0) return;

        const normalizedType =
          resourceType === FhirResourceTypes.MedicationStatement
            ? FhirResourceTypes.MedicationRequest
            : resourceType === FhirResourceTypes.Composition
            ? FhirResourceTypes.Encounter
            : resourceType;
        FhirResourceUtils.processFhirBundleResource(
          { entry: remainingResources.map((resource) => ({ resource })) },
          setter,
          normalizedType,
          setSelectedClinicalReports,
          setResourceBinaryItems,
          extractionContext
        );
      }
    });
  }, [fhirContextData, isFhirContextLoading]);

  useEffect(() => {
    const filesWithDescriptions: { file: File; description?: string }[] = [];

    for (const resource of resourceBinaryItems) {
      if (
        resource.data &&
        resource.id &&
        resource.contentType &&
        !uploadedIdsRef.current.has(resource.id)
      ) {
        const isDocumentReferenceBinary = docRefIds.has(resource.id);
        const resolvedName =
          binaryFileNameByIdRef.current[resource.id] || docRefFallbackFileNameById[resource.id];

        if (isDocumentReferenceBinary && !resolvedName && isDocRefBinaryFetchInProgress) {
          continue;
        }

        const fileName = FileUtils.appendExtension(
          resolvedName || resource.id,
          resource.contentType
        );
        const fileInfo = FileUtils.getFileFromBase64(resource.data, fileName, resource.contentType);
        filesWithDescriptions.push({
          file: fileInfo
        });
        uploadedIdsRef.current.add(resource.id);
      }
    }

    if (filesWithDescriptions.length > 0 && attachFileRef?.current) {
      attachFileRef.current.uploadFilesWithDescriptions(filesWithDescriptions);
    }
  }, [
    resourceBinaryItems,
    docRefIds,
    docRefFallbackFileNameById,
    binaryFileNamesVersion,
    isDocRefBinaryFetchInProgress
  ]);

  useEffect(() => {
    if (smartOnFhirPatientDetails && !caseDetails?.patient) {
      const ohip = smartOnFhirPatientDetails.entry?.[0]?.resource['identifier']?.find((i) => {
        return i.type?.text === 'OHIP';
      })?.value;
      setCaseDetails({
        ...caseDetails,
        patient: {
          firstName: smartOnFhirPatientDetails.entry?.[0]?.resource['name']?.[0]?.given?.[0] || '',
          lastName: smartOnFhirPatientDetails.entry?.[0]?.resource['name']?.[0]?.family || '',
          dateOfBirth: smartOnFhirPatientDetails.entry?.[0]?.resource['birthDate'] || '',
          ohipNumber: ohip,
          noOhip: !ohip,
          gender:
            smartOnFhirPatientDetails.entry?.[0]?.resource['gender']
              ?.substring(0, 1)
              ?.toUpperCase() || ''
        }
      });
    }
  }, [smartOnFhirPatientDetails, setCaseDetails, caseDetails]);

  useEffect(() => {}, [smartOnFhirPatientAllergyDetails]);

  useEffect(() => {
    if (isManagedSpecialtyError || isProgramLookupError) {
      setDisableActionButtons(false);
    } else if (isManagedSpecialtyFetching || isProgramLookupFetching) {
      setDisableActionButtons(true);
    } else {
      setDisableActionButtons(false);
    }
  }, [
    isManagedSpecialtyFetching,
    isProgramLookupFetching,
    isManagedSpecialtyError,
    isProgramLookupError
  ]);

  useEffect(() => {
    const seenUnavailability = searchParams.get('seenUnavailability');
    const specialistId = UrlService.getParamIgnoreCase(searchParams, 'specialistId');
    if (seenUnavailability && specialistId) {
      setAcknowledgedOutOfOffice(true);
    } else if (specialistId) {
      setAcknowledgedOutOfOffice(false);
    }

    if (
      myConsultants &&
      myConsultants.length > 0 &&
      specialistId &&
      !isSelectRequesterDialogOpen &&
      !caseDetails?.referrer?.userId
    ) {
      setTimeout(() => {
        setIsSelectRequesterDialogOpen(true);
      }, 1000);
    }
  }, [searchParams, myConsultants, isSelectRequesterDialogOpen, caseDetails?.referrer?.userId]);

  useEffect(() => {
    setDraftCaseSaved(false);
  }, [externalReferralContent, setDraftCaseSaved]);

  useEffect(() => {
    const updatedDetails = {
      ...caseDetails,
      ...(EconsultUtils.isSmartOnFhiUser(user?.source)
        ? {
            externalReferralId: UserSource.SmartOnFhir,
            externalReferralName: user?.system
          }
        : {}),
      ...(EconsultUtils.isSmartOnFhiUser(user?.source) ||
      caseDetails?.externalReferralContent ||
      hasExternalReferralContent
        ? { externalReferralContent: externalReferralContent }
        : {})
    };
    setUpdatedCaseDetails(updatedDetails);
    if (hasReceipient()) {
      setRecipientSelected(true);
    } else {
      setRecipientSelected(false);
    }
    const prevCase = getPreviousCaseDetails();
    if (caseDetails && (!prevCase || prevCase.caseId !== caseDetails.caseId)) {
      setPreviousCaseDetails(JSON.parse(JSON.stringify(caseDetails)));
    }
    const updCaseDetails = getUpdatedCaseDetails();
    if (
      updCaseDetails &&
      updCaseDetails.statusCd &&
      updCaseDetails.statusCd === 'DRAFT' &&
      hasCaseChanged(updCaseDetails, getPreviousCaseDetails())
    ) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handlePopState);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('popstate', handlePopState);
      };
    }
    if (
      caseDetails &&
      caseDetails.programType &&
      (caseDetails.programType === 'DIRECT' ||
        caseDetails.programType === 'MANAGED_SPECIALTY_GROUP')
    ) {
      setNonUrgentMessage(nonUrgentMessageHide);
    }
  }, [caseDetails, externalReferralContent, setUpdatedCaseDetails, setDraftCaseChanged]);

  useEffect(() => {
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    if (caseDetails?.programType) {
      setProgramTypeErrorMessageStyle(validFieldMessageType);
    }
    setOptions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseDetails?.programType]);

  useEffect(() => {
    if (caseDetails?.caseId?.toString() !== caseId) {
      setDraftFiles([]);
    } else if (
      caseDetails?.notes &&
      caseDetails.notes[0] &&
      caseDetails.notes[0].attachments &&
      caseDetails.notes[0].attachments.length > 0
    ) {
      const draftReplyNoteAttachmentFiles: FileWithProgress[] =
        caseDetails.notes[0].attachments.map((f) => {
          return {
            file: {
              name: f.uploadedFile?.fileName
            } as unknown as File,
            progress: 100,
            uploadedFile: f.uploadedFile,
            fileDescription: f.fileDescription,
            fileId: f.fileId,
            uid: crypto.randomUUID()
          };
        });
      if (draftReplyNoteAttachmentFiles && draftReplyNoteAttachmentFiles.length > 0) {
        setDraftFiles(draftReplyNoteAttachmentFiles);
      } else {
        setDraftFiles([]);
      }
    } else {
      setDraftFiles([]);
    }
    setSelectedTab(undefined);
  }, []);

  useEffect(() => {
    if (consultantsData && consultantsData.length === 1) {
      const delegateRequester = consultantsData.find((r) => r.roles?.econCaseCreate);
      if (delegateRequester) {
        setCaseDetails({
          ...caseDetails,
          referrer: delegateRequester as Referrer
        });
      }
    }
    let consultants = consultantsData || [];
    consultants = consultants
      .filter((c) => c.roles?.econCaseCreate)
      .sort((a, b) => a.firstName.localeCompare(b.firstName));
    setMyConsultants(consultants);
  }, [consultantsData]);

  useEffect(() => {
    const program = managedPrograms?.find((program) => {
      return program.region.id === selectedRegionId;
    });
    if (program) {
      setSelectedProgamId(program?.programId);
    }
  }, [selectedRegionId, managedPrograms]);

  useEffect(() => {
    const preselectProgram = async () => {
      const regions = managedPrograms?.map((program) => {
        return program.region;
      });

      if (
        participantProgram &&
        participantProgram.regionId &&
        caseDetails?.specialty?.code === prioritySpecialty?.code &&
        caseDetails?.subSpecialty?.subCode === prioritySubSpecialty?.subCode
      ) {
        const existingRegion = regions?.find((m) => m.id === participantProgram.regionId);
        if (!existingRegion) {
          const preselectedRegion = await fetchRegion(participantProgram.regionId.toString());
          regions?.push({
            id: preselectedRegion.id,
            isProvincial: preselectedRegion.isProvincial,
            lhin: preselectedRegion.lhin?.name,
            name: preselectedRegion.name
          });
        }
        setSelectedRegionId(participantProgram.regionId);
        setSelectedProgamId(participantProgram.programId);
      } else {
        setSelectedRegionId(regions?.[0]?.id);
      }
      setRegionList(regions);
    };
    preselectProgram();
  }, [
    managedPrograms,
    participantProgram,
    caseDetails?.specialty,
    caseDetails?.subSpecialty,
    prioritySpecialty?.code,
    prioritySubSpecialty?.subCode
  ]);

  const configureProgramRestrictions = (programProfile: ProgramProfile | undefined) => {
    if (
      !programProfile ||
      (!programProfile.econCondRegion &&
        !programProfile.econCondRegionText &&
        !programProfile.econCondition)
    )
      return;
    const restrictions: EconsultRestrictionNoteSection[] = [];
    let econsultRestrictions: EconsultRestrictionNoteSection = {
      title: 'eConsult Case Conditions',
      properties: []
    };
    let aboutGroupRestrictions: EconsultRestrictionNoteSection = {
      title: 'About the Group',
      properties: [
        {
          name: '',
          value: programProfile.programDescription
        }
      ]
    };
    if (programProfile.econCondRegion) {
      econsultRestrictions.properties.push({
        name: 'Only accepted from LHINs/Regions',
        value: programProfile.econCondRegion
      });
    }
    if (programProfile.econCondRegionText) {
      econsultRestrictions.properties.push({
        name: 'Only accepted from LHINs/Regions',
        value: programProfile.econCondRegionText
      });
    }
    if (programProfile.econCondition) {
      econsultRestrictions.properties.push({
        name: 'Other conditions',
        value: programProfile.econCondition
      });
    }

    if (econsultRestrictions && econsultRestrictions.properties.length > 0) {
      restrictions.push(econsultRestrictions);
    }
    if (aboutGroupRestrictions && aboutGroupRestrictions.properties.length > 0) {
      restrictions.push(aboutGroupRestrictions);
    }

    setProgramRestrictions(restrictions);
  };

  useEffect(() => {
    if (managedProgram) {
      configureProgramRestrictions(managedProgram);
      configureProgramRestrictions(managedProgram);
    }
  }, [managedProgram]);

  const configureRestrictions = (participant: any) => {
    let restrictions: EconsultRestrictionNoteSection[] = [];
    if (participant.specializedAreaOfPractice) {
      restrictions.push({
        title: 'Telemedicine Service Details',
        properties: [
          {
            name: '',
            value: participant.specializedAreaOfPractice
          }
        ]
      });
    }
    if (participant.econsult && (participant.catchmentArea || participant.otherConditions)) {
      let econsultRestriction: EconsultRestrictionNoteSection = {
        title: 'eConsult Case Conditions',
        properties: []
      };
      if (participant.econsult && participant.econsult.catchmentArea) {
        econsultRestriction.properties.push({
          name: 'Only accepted from LHINs/Regions',
          value: participant.econsult.catchmentArea
        });
      }
      if (participant.econsult && participant.econsult.otherConditions) {
        econsultRestriction.properties.push({
          name: 'Other conditions',
          value: participant.econsult.otherConditions
        });
      }
      if (econsultRestriction.properties.length > 0) {
        restrictions.push(econsultRestriction);
      }
    }
    if (participant.patientEligibility) {
      restrictions.push({
        title: 'Patient Eligibility',
        properties: [
          {
            name: '',
            value: participant.patientEligibility
          }
        ]
      });
    }
    setSpecialtyRestrictions(restrictions);
  };

  useEffect(() => {
    if (consultantProfile) {
      configureRestrictions(consultantProfile);
    }
  }, [consultantProfile]);

  useEffect(() => {
    if (participantProgram) {
      configureRestrictions(participantProgram);
      configureProgramRestrictions(participantProgram);
    }
  }, [participantProgram]);

  const handleModelLoad = (value: string) => {
    setManagedSpecialtyFontColor(unSelectedOptFontColor);
    seSpecificProviderFontColor(unSelectedOptFontColor);
    if (value === 'DIRECT') {
      setIsRegionSelectShown(false);
      seSpecificProviderFontColor(selectedOptFontColor);
    } else if (value === 'MANAGED_SPECIALTY_GROUP') {
      setManagedSpecialtyFontColor(selectedOptFontColor);
    }
  };

  useEffect(() => {
    handleModelLoad(caseDetails?.programType || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseDetails?.programType]);

  if (!caseDetails || caseDetails.caseId?.toString() !== caseId) {
    return null;
  }

  const { referrer, programType, notes } = caseDetails;

  const requestTextAreaStyle = {
    width: '100%',
    outline: 'none',
    border: 'none',
    resize: 'none' as 'none',
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.body2.fontSize,
    paddingTop: 20,
    height: 180,
    overflow: 'auto'
  };

  const handleSearchRecipientInputChange = async (inputValue: string) => {
    setIsRecipientError(false);
    setRecipientSelected(false);
    if (inputValue.length > 1) {
      const specialistsAndGroups = await CaseService.fetchCaseSpecialistsAndGroups(inputValue);
      setOptions(specialistsAndGroups);
      setShowError(true);
    } else {
      setOptions([]);
    }
  };

  const updateConsultant = (consultant: UserDTO | EconsultManagedSpecialtyProgram | null) => {
    const updatedCaseDetails = {
      ...caseDetails,
      participant: {
        ...caseDetails?.participant,
        consultant: consultant
          ? isUserDTO(consultant)
            ? consultant
            : caseDetails?.participant?.consultant
          : null,
        program: consultant
          ? isEconsultManagedSpecialtyProgram(consultant)
            ? consultant
            : caseDetails?.participant?.program
          : null
      }
    };
    setCaseDetails(updatedCaseDetails);
  };

  const isForwardingToSameRecipient = (
    consultant: UserDTO | EconsultManagedSpecialtyProgram | null
  ) => {
    if (
      !originalCaseDetails ||
      !originalCaseDetails.data ||
      !consultant ||
      hasCaseBeenAutoReturnedBySystem()
    ) {
      return false;
    }
    if (isUserDTO(consultant)) {
      const consultantUser = consultant as UserDTO;
      if (originalCaseDetails?.data?.participant?.consultant?.userId === consultantUser?.userId) {
        return true;
      }
    } else if ('programName' in consultant) {
      const program = consultant as EconsultManagedSpecialtyProgram;
      if (originalCaseDetails?.data?.participant?.program?.programId === program?.programId) {
        return true;
      }
    }
    return false;
  };

  const handleConsultantChanged = (
    consultant: UserDTO | EconsultManagedSpecialtyProgram | null
  ) => {
    if (!consultant) {
      setOptions([]);
      setAcknowledgedOutOfOffice(false);
    }
    if (isForwardingToSameRecipient(consultant)) {
      setIsRecipientError(true);
      setOptions([]);
      setTimeout(() => {
        specialistClearRef.current?.clear();
      });
      return;
    } else {
      setIsRecipientError(false);
    }
    if (isUserDTO(consultant)) {
      const consultantUser = consultant as UserDTO;
      if (consultantUser?.unavailability) {
        const unavailableDate = EconsultUtils.getOutOfOfficeDateInAWeek(
          consultantUser.unavailability
        );
        if (unavailableDate) {
          setOutOfOfficeConsultant(consultant);
          setConsultOutOfOfficeDate(unavailableDate);
          return;
        }
      }
      setSelectedProgamId(undefined);
    } else if (isEconsultManagedSpecialtyProgram(consultant)) {
      setSelectedProgamId(consultant.programId);
    }
    console.log(`consultant changed: ${JSON.stringify(consultant)}`);
    setRecipientSelected(true);
    updateConsultant(consultant);
    setShowError(true);
    setDraftCaseChanged(true);
  };
  const handleOutOfOfficeContinue = async () => {
    if (outOfOfficeConsultant) {
      updateConsultant(outOfOfficeConsultant);
      setOutOfOfficeConsultant(undefined);
      setConsultOutOfOfficeDate(undefined);
      setAcknowledgedOutOfOffice(true);
    }
    if (isSending) {
      setIsSending(false);
      await sendDraftCase();
    }
  };
  const handleOutOfOfficeCancel = () => {
    setOutOfOfficeConsultant(undefined);
    setConsultOutOfOfficeDate(undefined);
    specialistClearRef.current?.clear();
    updateConsultant(null);
    setAcknowledgedOutOfOffice(false);
    setIsSending(false);
  };
  const handleProgramTypeChanged = async (event: ChangeEvent<HTMLInputElement>, value: string) => {
    if (!value) {
      return;
    }
    const updatedCaseDetails = {
      ...caseDetails,
      programType: value
    };
    setIsRecipientError(false);
    setManagedSpecialtyFontColor(unSelectedOptFontColor);
    seSpecificProviderFontColor(unSelectedOptFontColor);
    if (value === 'DIRECT') {
      delete updatedCaseDetails?.specialty;
      delete updatedCaseDetails?.subSpecialty;
      setIsRegionSelectShown(false);
      seSpecificProviderFontColor(selectedOptFontColor);
    } else if (value === 'MANAGED_SPECIALTY_GROUP') {
      setManagedSpecialtyFontColor(selectedOptFontColor);
      if (!user?.seenPriorityRegion) {
        setIsPriorityRegionDialogOpen(true);
      }
    }
    delete updatedCaseDetails?.participant;
    setSelectedProgamId(undefined);
    setProgramRestrictions([]);
    setSpecialtyRestrictions([]);
    setNonUrgentMessage(nonUrgentMessageHide);
    setCaseDetails(updatedCaseDetails);
  };

  const handleNoteContentChanged = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    const { name } = event.target;
    const fieldName = { name }.name;
    const res: ValidationResult = ValidatorUtils.handleValidation(
      requiredFields,
      dirtyFields,
      invalidFields,
      fieldName,
      value
    );
    setFieldDiryty(res.dirtyFields);
    setFieldInvalid(res.invalidFields);

    const valid = res.isValid;

    if (valid) {
      setRequestNteErrorMessageStyle(validFieldMessageType);
    } else {
      setRequestNteErrorMessageStyle(inVaildFieldMessageType);
    }

    const updatedCaseDetails = {
      ...caseDetails,
      notes: [
        ...(caseDetails.notes
          ? [
              {
                ...caseDetails.notes[0],
                noteContent: value
              }
            ]
          : []),
        ...(caseDetails.notes ? caseDetails.notes.slice(1) : [])
      ]
    };
    setCaseDetails(updatedCaseDetails);
  };
  const isReferrerConsultantSame = (caseObj: CaseDetails) => {
    if (!caseObj) {
      return false;
    }
    if (caseObj.referrerId && caseObj.participant && caseObj.participant.consultant) {
      return caseObj.referrerId === caseObj.participant.consultant.userId;
    }
    return false;
  };
  const handleDraftSaved = async (hideNotification?: boolean) => {
    setDisableActionButtons(true);
    setDraftCaseSaveDisable(true);
    const updatedCaseDetails = {
      ...caseDetails,
      participant: managedProgram
        ? {
            program: {
              ...managedProgram
            },
            statusCd: NoteStatusCodes.draft,
            triageStatusCd: NoteStatusCodes.draft
          }
        : {
            participantId: caseDetails?.participant?.participantId || null,
            ...caseDetails.participant,
            statusCd: NoteStatusCodes.draft,
            ...(caseDetails?.participant?.program?.programId
              ? { triageStatusCd: NoteStatusCodes.draft }
              : {})
          },
      notes: caseDetails?.notes?.length
        ? [
            {
              ...caseDetails.notes[0]
            },
            ...caseDetails.notes.slice(1)
          ]
        : undefined,
      ...(EconsultUtils.isSmartOnFhiUser(user?.source)
        ? {
            externalReferralId: UserSource.SmartOnFhir,
            externalReferralName: user?.system
          }
        : {}),
      ...(EconsultUtils.isSmartOnFhiUser(user?.source) || hasExternalReferralContent
        ? { externalReferralContent: externalReferralContent }
        : {})
    };
    setCaseDetails(updatedCaseDetails);
    removeAllErrorNotifications();
    try {
      if (isReferrerConsultantSame(updatedCaseDetails)) {
        addNotification({
          type: 'error',
          message: t('Case.draft.sameRecipentSpecialist')
        });
        setDisableActionButtons(false);
        setDraftCaseSaveDisable(false);
        return;
      }
      await CaseService.upsertCaseDetails(updatedCaseDetails);
    } catch (error: any) {
      const msg = error?.response?.data?.details;
      if (error?.response?.data?.error === 'E_CASE_VALIDATION_ERROR_REFERRER_SPECIALIST') {
        addNotification({
          type: 'error',
          message: msg || t('Case.draft.invalidDelegateRecipent2')
        });
      } else {
        addNotification({
          type: 'error',
          message: msg || t('Case.connection.lost')
        });
      }
      setDisableActionButtons(false);
      setDraftCaseSaveDisable(false);
      return;
    }
    setPreviousCaseDetails(JSON.parse(JSON.stringify(updatedCaseDetails)));
    setTimeout(() => {
      queryClient.invalidateQueries(['caseResults']);
    }, 500);
    if (!hideNotification) {
      addNotification(
        NotificationFactory.createNotification(
          'success',
          NotificationsConfig.econsultSaveDraft.success
        )
      );
    }
    setDraftCaseSaved(true);
    setDisableActionButtons(false);
    setDraftCaseSaveDisable(false);
  };

  const getSaveDraftDisabled = () => {
    return draftCaseSaveDisable;
  };

  const scrollToAttachmentSection = () => {
    if (attachmentSectionRef && attachmentSectionRef.current) {
      attachmentSectionRef.current.scrollIntoView({ block: 'nearest' });
    }
  };

  const handleOnStartAttachment = () => {
    setDisableActionButtons(true);
    scrollToAttachmentSection();
  };

  const handleOnCompleteFileUpload = () => {
    setDisableActionButtons(false);
  };

  const isDelegateUserRecipientValid = (): boolean => {
    if (user) {
      const isDelegate = EconsultUserService.isEconsultDelegate(user);
      if (isDelegate && referrer?.userId === caseDetails?.participant?.consultant?.userId) {
        return false;
      }
    }
    return true;
  };

  const isCaseValid = (): boolean => {
    let isCaseDetailsValid: boolean = true;

    if (caseDetails && caseDetails?.programType) {
      if (caseDetails.programType === 'MANAGED_SPECIALTY_GROUP') {
        if (specialityRef.current) {
          isCaseDetailsValid = specialityRef.current.validate();
        }
      } else if (caseDetails.programType === 'DIRECT') {
        if (specialistClearRef.current) {
          isCaseDetailsValid = specialistClearRef.current.validate();
        } else {
          isCaseDetailsValid = false;
        }
      }
    }

    return isCaseDetailsValid;
  };
  const isPatientFormValid = (): boolean => {
    let isPatientDetailsValid: boolean = true;
    if (caseDetails.patient && caseDetails.patient.ohipNumber) {
      caseDetails.patient.noOhip = false;
    }
    if (
      !OhipValidationService.isOhipNumberValid(caseDetails?.patient?.ohipNumber) &&
      !caseDetails?.patient?.noOhip
    ) {
      isPatientDetailsValid = false;
    }
    if (patientFormRef.current) {
      const isPatientValid = patientFormRef.current.validate();
      isPatientDetailsValid = isPatientValid;
    }
    const isRequestNteValid = caseDetails.notes?.[0].noteContent;

    if (isRequestNteValid) {
      setRequestNteErrorMessageStyle(validFieldMessageType);
    } else {
      setRequestNteErrorMessageStyle(inVaildFieldMessageType);
      isPatientDetailsValid = false;
    }

    setFieldInvalid(invalidFields);
    return isPatientDetailsValid;
  };
  const handleDraftDeleteConfirm = async () => {
    const updatedCaseDetails: CaseDetails = {
      ...caseDetails,
      statusCd: NoteStatusCodes.deleted
    };
    setCaseDetails(updatedCaseDetails);
    removeAllErrorNotifications();
    try {
      await CaseService.upsertCaseDetails(updatedCaseDetails);
    } catch (error: any) {
      const msg = error?.response?.data?.details;
      addNotification({
        type: 'error',
        message: msg || t('Case.draft.failedSave')
      });
      return;
    }
    addNotification(
      NotificationFactory.createNotification(
        'success',
        NotificationsConfig.econsultDeleteDraft.success
      )
    );
    setTimeout(() => {
      queryClient.invalidateQueries(['caseResults']);
    }, 300);
    const draftInboxUrl = `/case/${NoteTypes.referrer}/${NoteActionTypes.draft}`;
    navigate(draftInboxUrl.toLowerCase());
  };
  const handleDraftDeleted = () => {
    setIsDeleteDraftDialogOpen(true);
  };

  const handleFileAttach = () => {
    if (attachFileRef && attachFileRef.current) {
      attachFileRef.current.attachFile();
    }
  };

  const sendDraftCase = async () => {
    if (isSubmitting.current) {
      return;
    }
    isSubmitting.current = true;
    if (autoSaveInProgress.current) {
      try {
        await autoSaveInProgress.current;
      } catch {}
    }
    removeAllErrorNotifications();
    if (!managedProgram && !caseDetails?.participant?.consultant) {
      addNotification({
        type: 'error',
        message: t('Case.draft.invalidRecipient'),
        id: caseId ? parseInt(caseId) : 1
      });
      setCaseDetails({
        ...caseDetails,
        participant: undefined,
        subSpecialty: undefined,
        specialty: undefined
      });
      specialistClearRef.current?.clear();
    }
    setDisableActionButtons(true);
    const updatedCaseDetails: CaseDetails = {
      ...caseDetails,
      statusCd: NoteStatusCodes.active,
      participant: managedProgram
        ? {
            program: {
              ...managedProgram
            },
            statusCd: NoteStatusCodes.active,
            triageStatusCd: NoteStatusCodes.active
          }
        : {
            participantId: caseDetails?.participant?.participantId || null,
            ...caseDetails.participant,
            statusCd: NoteStatusCodes.active,
            ...(caseDetails?.participant?.program?.programId
              ? { triageStatusCd: NoteStatusCodes.active }
              : {})
          },
      notes: [
        ...(caseDetails.notes
          ? [
              {
                ...caseDetails.notes[0],
                ownerId: user?.userid,
                statusCd: NoteStatusCodes.active,
                noteTypeCd: NoteTypes.referrer,
                actionTypeCd: NoteActionTypes.send
              }
            ]
          : []),
        ...(caseDetails.notes ? caseDetails.notes.slice(1) : [])
      ],
      ...(EconsultUtils.isSmartOnFhiUser(user?.source)
        ? {
            externalReferralId: UserSource.SmartOnFhir,
            externalReferralName: user?.system
          }
        : {}),
      ...(EconsultUtils.isSmartOnFhiUser(user?.source) || hasExternalReferralContent
        ? { externalReferralContent: externalReferralContent }
        : {})
    };
    setUpdatedCaseDetails(undefined);
    removeAllErrorNotifications();
    try {
      if (
        !updatedCaseDetails?.participant?.program &&
        !updatedCaseDetails?.participant?.consultant
      ) {
        addNotification({
          type: 'error',
          message: t('Case.draft.invalidRecipient'),
          id: caseId ? parseInt(caseId) : 1
        });
        setCaseDetails({
          ...caseDetails,
          participant: undefined,
          subSpecialty: undefined,
          specialty: undefined
        });
        specialistClearRef.current?.clear();
        isSubmitting.current = false;
        return;
      }
      await CaseService.upsertCaseDetails(updatedCaseDetails);
    } catch (error: any) {
      const msg = error?.response?.data?.details;
      if (error?.response?.data?.error === 'E_CASE_VALIDATION_ERROR_REFERRER_SPECIALIST') {
        addNotification({
          type: 'error',
          message: msg || t('Case.draft.invalidDelegateRecipent')
        });
      } else {
        addNotification({
          type: 'error',
          message: msg || t('Case.draft.failedSave')
        });
      }
      setDisableActionButtons(false);
      isSubmitting.current = false;
      return;
    }
    addNotification(
      NotificationFactory.createNotification(
        'success',
        NotificationsConfig.econsultDraftConsult.success
      )
    );
    setDisableActionButtons(false);
    isSubmitting.current = false;
    setTimeout(() => {
      queryClient.invalidateQueries(['caseResults']);
    }, 300);
    const draftInboxUrl = `/case/${NoteTypes.referrer}/${NoteActionTypes.draft}`;
    navigate(draftInboxUrl.toLowerCase());
  };

  const handleDraftSend = async () => {
    const isPatientValid = isPatientFormValid();
    const isCaseDataValid = isCaseValid();
    const isDelegateUserRecipientDataValid = isDelegateUserRecipientValid();
    setShowError(false);
    removeAllErrorNotifications();
    if (!isPatientValid || !isCaseDataValid) {
      if (patientFormDetailsSectionRef && patientFormDetailsSectionRef.current) {
        patientFormDetailsSectionRef.current.scrollIntoView();
      } else if (
        !caseDetails.notes?.[0].noteContent &&
        requestNoteSectionRef &&
        requestNoteSectionRef.current
      ) {
        requestNoteSectionRef.current.scrollIntoView();
      }
      return;
    }
    if (isReferrerConsultantSame(caseDetails)) {
      addNotification({
        type: 'error',
        message: t('Case.draft.sameRecipentSpecialist')
      });
      return;
    }
    if (!isDelegateUserRecipientDataValid) {
      addNotification({
        type: 'error',
        message: t('Case.draft.invalidDelegateRecipent')
      });
      return;
    }
    const selectedRegion = regionList?.find((r) => r.id === selectedRegionId);
    if (isForwardingToSameManagedProgram(originalCaseDetails?.data, selectedRegion)) {
      addNotification({
        type: 'error',
        message: t('Case.draft.recipientError'),
        id: selectedRegionId
      });
      setOptions([]);
      return;
    }
    if (!caseDetails.programType) {
      setProgramTypeErrorMessageStyle(inVaildFieldMessageType);
      if (patientFormDetailsSectionRef && patientFormDetailsSectionRef.current) {
        patientFormDetailsSectionRef.current.scrollIntoView();
      }
      return;
    }
    if (caseDetails?.participant?.consultant && !acknowledgedOutOfOffice) {
      var unavailability = settings?.find((s) => s.name === 'UNAVAILABILITY');
      if (unavailability && unavailability.value) {
        const unavailableToday = EconsultUtils.getOutOfOfficeToday(unavailability.value);
        const unavailableDate = EconsultUtils.getOutOfOfficeDateInAWeek(unavailability.value);
        if (unavailableToday) {
          addNotification({
            type: 'error',
            message: t('Case.draft.specialistUnavailable')
          });
          if (specialistClearRef && specialistClearRef.current) {
            specialistClearRef.current.clear();
          }
          return;
        }
        if (unavailableDate) {
          setOutOfOfficeConsultant(caseDetails?.participant?.consultant);
          setConsultOutOfOfficeDate(unavailableDate);
          setIsSending(true);
          return;
        }
      }
    }
    await sendDraftCase();
  };
  const handleViewProgram = () => {
    if (selectedProgramId) {
      RedirectService.redirectToProgramProfile(selectedProgramId.toString());
    }
  };
  const handleViewPeopleProfile = () => {
    if (caseDetails?.participant?.consultant?.contactId) {
      RedirectService.redirectToDirectoryPeopleProfile(
        caseDetails.participant.consultant.contactId.toString()
      );
    } else if (caseDetails?.participant?.program?.programId) {
      RedirectService.redirectToProgramProfile(caseDetails?.participant?.program?.programId);
    }
  };
  const handleSkipPriorityRegion = () => {
    setIsPriorityRegionDialogOpen(false);
  };
  const handleSetPriorityRegion = () => {
    window.open(
      `${AppConfig.baseSelfServeUrl}/#/serviceSettings/econsult`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleDelegatorUserSelected = (userId: string) => {
    const delegator = myConsultants.find((d) => d.userId.toString() === userId.toString());
    if (delegator) {
      setCaseDetails({
        ...caseDetails,
        referrer: {
          userId: delegator.userId,
          firstName: delegator.firstName,
          lastName: delegator.lastName,
          roles: {
            econCaseCreate: Boolean(delegator.roles?.econCaseCreate)
          }
        },
        ...(caseDetails.specialty && { specialty: undefined }),
        ...(caseDetails.subSpecialty && { subSpecialty: undefined })
      });
    }
    setSelectedProgamId(undefined);
    setIsSelectRequesterDialogOpen(false);
  };
  const onDraftWarningDialogCancelled = () => {
    setIsDraftWarningDialogOpen(false);
    setUpdatedCaseDetails(undefined);
    abortAllUploads();
    navigate(-1); // Allow navigation back
  };
  const onDraftWarningDialogConfirmed = () => {
    setIsDraftWarningDialogOpen(false);
    window.history.pushState(null, '', window.location.href);
  };

  const handleSelectedRequesterCancel = () => {
    setIsSelectRequesterDialogOpen(false);
    navigate('/case/nodelegate');
  };

  const handlePrioritySpecialtyChanged = (specialty: ProgramSpecialty | undefined) => {
    setPrioritySpecialty(specialty);
  };

  const handlePrioritySubSpecialtyChanged = (subSpecialty: ProgramSpecialty | undefined) => {
    setPrioritySubSpecialty(subSpecialty);
  };

  const handleSpecialtyChanged = () => {
    setIsRegionSelectShown(false);
    setSelectedProgamId(undefined);
  };

  const handleDeleteCategorySectionCancel = () => {
    setIsDeleteCategorySectionDialogOpen(false);
    setClinicReportToDelete(null);
  };

  const handleObservationSectionChanged = (
    section: 'laboratory' | 'pathology' | 'other',
    value: string
  ) => {
    editedClinicalReportTypesRef.current.add(FhirResourceTypes.Observation);
    setObservationContent((prev) =>
      joinObservationContent({ ...splitObservationContent(prev), [section]: value })
    );
  };

  const handleDeleteCategorySectionConfirmation = () => {
    if (clinicReportToDelete === OBSERVATION_LABORATORY_SECTION) {
      handleObservationSectionChanged('laboratory', '');
    } else if (clinicReportToDelete === OBSERVATION_PATHOLOGY_SECTION) {
      handleObservationSectionChanged('pathology', '');
    } else if (clinicReportToDelete) {
      editedClinicalReportTypesRef.current.add(clinicReportToDelete);
      setDeletedReportTypes((prev) => new Set(prev).add(clinicReportToDelete));
      setSelectedClinicalReports((prev) =>
        prev.filter((report) => report !== clinicReportToDelete)
      );
    }
    setIsDeleteCategorySectionDialogOpen(false);
    setClinicReportToDelete(null);
  };

  const handleOnCategorySectionDeleteClicked = (clinicReport: string) => {
    setClinicReportToDelete(clinicReport);
    setIsDeleteCategorySectionDialogOpen(true);
  };

  const observationParts = splitObservationContent(
    getEffectiveContent(FhirResourceTypes.Observation, observationContent)
  );

  const bySectionType = (type: string) => (value: string) =>
    handleClinicalReportContentChanged(type, value);

  const CLINICAL_REPORT_SECTIONS: ClinicalReportSection[] = [
    {
      key: OBSERVATION_LABORATORY_SECTION,
      labelKey: 'ClinicalReports.laboratory',
      content: observationParts.laboratory,
      onContentChanged: (value) => handleObservationSectionChanged('laboratory', value),
      readOnly: true,
      isLabResult: true
    },
    {
      key: OBSERVATION_PATHOLOGY_SECTION,
      labelKey: 'ClinicalReports.pathology',
      content: observationParts.pathology,
      onContentChanged: (value) => handleObservationSectionChanged('pathology', value),
      readOnly: true,
      isPathologyReport: true
    },
    {
      key: FhirResourceTypes.DiagnosticReport,
      labelKey: 'ClinicalReports.diagnosticReports',
      content: getEffectiveContent(FhirResourceTypes.DiagnosticReport, diagnosticReportContent),
      onContentChanged: bySectionType(FhirResourceTypes.DiagnosticReport),
      readOnly: true,
      isDiagnosticReport: true
    },
    {
      key: FhirResourceTypes.MedicationRequest,
      labelKey: 'ClinicalReports.medications',
      content: getEffectiveContent(FhirResourceTypes.MedicationRequest, medicationContent),
      onContentChanged: bySectionType(FhirResourceTypes.MedicationRequest)
    },
    {
      key: FhirResourceTypes.Condition,
      labelKey: 'ClinicalReports.condition',
      content: getEffectiveContent(FhirResourceTypes.Condition, conditionContent),
      onContentChanged: bySectionType(FhirResourceTypes.Condition)
    },
    {
      key: FhirResourceTypes.Procedure,
      labelKey: 'ClinicalReports.procedures',
      content: getEffectiveContent(FhirResourceTypes.Procedure, procedureContent),
      onContentChanged: bySectionType(FhirResourceTypes.Procedure)
    },
    {
      key: FhirResourceTypes.FamilyMemberHistory,
      labelKey: 'ClinicalReports.familyMemberHistory',
      content: getEffectiveContent(FhirResourceTypes.FamilyMemberHistory, historyContent),
      onContentChanged: bySectionType(FhirResourceTypes.FamilyMemberHistory)
    },
    {
      key: FhirResourceTypes.AllergyIntolerance,
      labelKey: 'ClinicalReports.allergyIntolerance',
      content: getEffectiveContent(FhirResourceTypes.AllergyIntolerance, allergyContent),
      onContentChanged: bySectionType(FhirResourceTypes.AllergyIntolerance)
    },
    {
      key: FhirResourceTypes.Immunization,
      labelKey: 'ClinicalReports.immunizations',
      content: getEffectiveContent(FhirResourceTypes.Immunization, immunizationContent),
      onContentChanged: bySectionType(FhirResourceTypes.Immunization)
    },
    {
      key: FhirResourceTypes.Observation,
      labelKey: 'ClinicalReports.observations',
      content: expandObservationSpacing(stripSocialHistoryHeading(observationParts.other)),
      onContentChanged: (value) =>
        handleObservationSectionChanged('other', collapseObservationSpacing(value))
    },
    {
      key: FhirResourceTypes.Encounter,
      labelKey: 'ClinicalReports.encounters',
      content: getEffectiveContent(FhirResourceTypes.Encounter, encounterContent),
      onContentChanged: bySectionType(FhirResourceTypes.Encounter),
      readOnly: true,
      isEncounter: true
    }
  ];

  const visibleClinicalReportSections = CLINICAL_REPORT_SECTIONS.filter(
    ({ content }) => content !== ''
  );
  const isClinicalReportsLoading = isFhirContextLoading || isLoadingSmartOnFhirData;

  return (
    <>
      <Stack direction="column" p={4} ref={formRef}>
        <Grid container spacing={0}>
          <DraftDetailsSection item xs={12} md={2}>
            <Typography variant="body2" color="base.grey4">
              {t('Case.draft.details.view.requester')}
            </Typography>
          </DraftDetailsSection>
          <DraftDetailsSection item xs={12} md={10}>
            {myConsultants && myConsultants.length > 1 ? (
              <EconsultDelegatorSelector
                options={myConsultants}
                onUserSelected={handleDelegatorUserSelected}
                userId={caseDetails?.referrer?.userId?.toString() || '0'}
                hideOutline
              />
            ) : (
              <Typography variant="body2" color="primary.main" mb={2}>
                {referrer &&
                  NameUtils.makeName(
                    referrer.lastName || '',
                    referrer.firstName,
                    referrer.salutation,
                    referrer.specialty,
                    null,
                    null
                  )}
              </Typography>
            )}
          </DraftDetailsSection>
          <DraftDetailsSection item xs={12} md={2}>
            <Box display="flex" alignItems="center" pt={1}>
              <Typography
                variant="body2"
                color="base.grey4"
                sx={{
                  '&:before': {
                    color: '#F19F53',
                    content: '"* "',
                    fontWeight: 'bold',
                    fontSize: '18px'
                  }
                }}>
                {t('Case.detials.model')}
              </Typography>

              <HtmlTooltip
                placement="left"
                title={
                  <>
                    <b>BASE Managed Specialty:</b> This option enables you to submit cases to either
                    a regional (where available) or provincial managed specialty group, which is a
                    group of specialists responding to eConsult cases for a given specialty or
                    sub-specialty (e.g. pediatric cardiology).
                    <br />
                    <br />
                    <b>Specific Provider or Group:</b> This option allows you to submit cases to
                    specific consultants by name or to organizational or regional groups (ex. UHN's
                    SCOPE group). Cases are sent directly to the consultant or group.
                  </>
                }>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '20px',
                    height: '20px',
                    backgroundColor: '#999999',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    borderRadius: '50%',
                    marginLeft: '5px',
                    cursor: 'pointer'
                  }}>
                  ?
                </Box>
              </HtmlTooltip>
            </Box>
          </DraftDetailsSection>
          <DraftDetailsSection item xs={12} md={10} ref={patientFormDetailsSectionRef}>
            <FormControl>
              <RadioGroup
                aria-labelledby="demo-form-control-label-placement"
                name="position"
                onChange={handleProgramTypeChanged}
                value={programType || ''}>
                <FormControlLabel
                  value="MANAGED_SPECIALTY_GROUP"
                  control={
                    <Radio
                      sx={{
                        '& .MuiSvgIcon-root:not(.MuiSvgIcon-root ~ .MuiSvgIcon-root)': {
                          color: managedSpecialtyFontColor
                        },
                        '& .MuiSvgIcon-root + .MuiSvgIcon-root': {
                          color: managedSpecialtyFontColor
                        }
                      }}
                    />
                  }
                  label="BASE Managed Specialty"
                  labelPlacement="end"
                  sx={{
                    color: managedSpecialtyFontColor,
                    fontSize: '0.875rem',
                    '& .MuiTypography-root': {
                      fontSize: '0.875rem'
                    }
                  }}
                />
                <FormControlLabel
                  value="DIRECT"
                  control={
                    <Radio
                      sx={{
                        '& .MuiSvgIcon-root:not(.MuiSvgIcon-root ~ .MuiSvgIcon-root)': {
                          color: specificProviderFontColor
                        },
                        '& .MuiSvgIcon-root + .MuiSvgIcon-root': {
                          color: specificProviderFontColor
                        }
                      }}
                    />
                  }
                  label="Specific Provider or Group"
                  labelPlacement="end"
                  sx={{
                    color: specificProviderFontColor,
                    fontSize: '0.875rem',
                    '& .MuiTypography-root': {
                      fontSize: '0.875rem'
                    }
                  }}
                />
              </RadioGroup>
            </FormControl>
            <Typography id="programTypeErrorMessageStyle" style={programTypeErrorMessageStyle}>
              {t('Case.patient.field.required')}
            </Typography>
            <Typography id="nonUrgentMessage" style={nonUrgentMessage}>
              Reminder: eConsult is for non-urgent cases only. Specialists are expected to respond
              within 7 days, but reassignment may extend response times.
            </Typography>
          </DraftDetailsSection>
          {(caseDetails?.programType || caseDetails?.patient) && (
            <>
              {caseDetails?.programType === 'DIRECT' && (
                <>
                  <DraftDetailsSection item xs={12} md={2}>
                    <RequiredFieldTypography variant="body2" color="base.grey4" pt={2}>
                      {t('Case.draft.details.view.recipient')}
                    </RequiredFieldTypography>
                  </DraftDetailsSection>
                  <DraftDetailsSection item xs={12} md={10}>
                    <EconsultSpecialistSearch
                      options={isRecipientError || recipientSelected ? [] : options}
                      onInputChange={handleSearchRecipientInputChange}
                      onSelectionChange={handleConsultantChanged}
                      ref={specialistClearRef}
                    />
                    {isRecipientError && showError && (
                      <Typography variant="body2" color="red">
                        {t('Case.draft.recipientError')}
                      </Typography>
                    )}
                    {caseDetails.participant &&
                      caseDetails.participant.consultant &&
                      specialtyRestriction &&
                      specialtyRestriction.length > 0 && (
                        <>
                          <EconsultRestrictionNote
                            note={t('Case.consult.specialistRestrictionsNote')}
                            properties={specialtyRestriction}
                          />
                        </>
                      )}
                    {caseDetails.participant &&
                      caseDetails.participant.program &&
                      'programName' in caseDetails.participant.program &&
                      programRestrictions &&
                      programRestrictions.length > 0 && (
                        <>
                          <EconsultRestrictionNote
                            note={t('Case.consult.restrictionsNote')}
                            properties={programRestrictions}
                          />
                        </>
                      )}
                    {(caseDetails?.participant?.consultant || caseDetails?.participant?.program) &&
                      !EconsultUtils.isSmartOnFhiUser(user?.source) && (
                        <Button variant="text" sx={{ pl: 0 }} onClick={handleViewPeopleProfile}>
                          {t('Case.consult.viewDirectoryProfile')}
                        </Button>
                      )}
                  </DraftDetailsSection>
                </>
              )}
              {caseDetails?.programType === 'MANAGED_SPECIALTY_GROUP' && (
                <>
                  <DraftDetailsSection item xs={12} md={2}>
                    <RequiredFieldTypography variant="body2" color="base.grey4" pt={1}>
                      {t('Case.draft.details.view.speciality')}
                    </RequiredFieldTypography>
                  </DraftDetailsSection>
                  <DraftDetailsSection item xs={12} md={10}>
                    <EconsultCaseSpecialtySection
                      ref={specialityRef}
                      onSpecialtyChange={handleSpecialtyChanged}
                      prioritySpecialty={prioritySpecialty}
                      prioritySubSpecialty={prioritySubSpecialty}
                      onPrioritySpecialtyChanged={handlePrioritySpecialtyChanged}
                      onPrioritySubSpecialtyChanged={handlePrioritySubSpecialtyChanged}
                    />
                  </DraftDetailsSection>
                  <DraftDetailsSection item xs={12} md={2}>
                    <Box display="flex" alignItems="center" pt={1}>
                      <Typography
                        variant="body2"
                        color="base.grey4"
                        sx={{
                          '&:before': {
                            color: '#F19F53',
                            content: '"* "',
                            fontWeight: 'bold',
                            fontSize: '18px'
                          }
                        }}>
                        {t('Case.draft.details.view.region')}
                      </Typography>

                      <HtmlTooltip
                        placement="left"
                        title={
                          <React.Fragment>
                            {t('Case.draft.details.view.region.tool.tip')}
                          </React.Fragment>
                        }>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '20px',
                            height: '20px',
                            backgroundColor: '#999999',
                            color: '#ffffff',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            borderRadius: '50%',
                            marginLeft: '5px',
                            cursor: 'pointer'
                          }}>
                          ?
                        </Box>
                      </HtmlTooltip>
                    </Box>
                  </DraftDetailsSection>
                  <DraftDetailsSection item xs={12} md={10}>
                    <Stack direction="row" alignItems="center">
                      {regionList && regionList.length > 0 && (
                        <>
                          {!isRegionSelectShown ? (
                            <>
                              <Typography>{selectedRegionName}</Typography>
                              {regionList.length > 1 && (
                                <Button
                                  variant="text"
                                  sx={{ ml: 3 }}
                                  onClick={() => setIsRegionSelectShown(true)}>
                                  Change
                                </Button>
                              )}
                            </>
                          ) : (
                            <Select
                              value={selectedRegionId}
                              onChange={(e) =>
                                setSelectedRegionId(e.target.value as number | undefined)
                              }>
                              {regionList.map((region) => (
                                <MenuItem value={region.id}>{region.name}</MenuItem>
                              ))}
                            </Select>
                          )}
                        </>
                      )}
                    </Stack>
                    {selectedProgramId &&
                      (managedProgram?.econCondRegionText || managedProgram?.econCondition) && (
                        <>
                          <EconsultRestrictionNote
                            note={t('Case.consult.restrictionsNote')}
                            properties={programRestrictions}
                          />
                        </>
                      )}
                    {regionList &&
                      regionList.length > 0 &&
                      !EconsultUtils.isSmartOnFhiUser(user?.source) && (
                        <Button variant="text" sx={{ pl: 0 }} onClick={handleViewProgram}>
                          {t('Case.consult.viewDirectoryProfile')}
                        </Button>
                      )}
                  </DraftDetailsSection>
                </>
              )}
              <DraftDetailsSection item xs={12} md={2}>
                <Typography variant="body2" color="base.grey4">
                  {t('Case.draft.details.view.patient')}
                </Typography>
              </DraftDetailsSection>
              <DraftDetailsSection item xs={12} md={10}>
                <EconsultCasePatientForm ref={patientFormRef} />
              </DraftDetailsSection>
              <DraftDetailsSection item xs={12} md={2}>
                <RequiredFieldTypography variant="body2" color="base.grey4" pt={2}>
                  {t('Case.draft.details.view.request')}
                </RequiredFieldTypography>
              </DraftDetailsSection>
              <DraftDetailsSection item xs={12} md={10}>
                <StyledTextAreaAutoResize
                  ref={requestNoteSectionRef}
                  onChange={handleNoteContentChanged}
                  placeholder={t('Case.draft.requestPlaceholder') as string}
                  maxRows={10}
                  value={notes?.[0]?.noteContent || ''}
                  style={requestTextAreaStyle}
                  name="requestNte"
                />
                <Typography id="requestNteValidationdMessage" style={requestNteErrorMessageStyle}>
                  {t('Case.patient.field.required')}
                </Typography>
              </DraftDetailsSection>
              {(fhirPatientGuid ||
                fhirContext ||
                caseDetails?.externalReferralId?.toLowerCase() ===
                  UserSource.SmartOnFhir?.toLowerCase() ||
                (caseDetails?.externalReferralContent &&
                  Object.keys(JSON.parse(caseDetails.externalReferralContent)).length > 0)) && (
                <>
                  {visibleClinicalReportSections.map(
                    ({
                      key,
                      labelKey,
                      content,
                      onContentChanged,
                      readOnly,
                      isLabResult,
                      isPathologyReport,
                      isDiagnosticReport,
                      isEncounter
                    }) => (
                      <React.Fragment key={key}>
                        <DraftDetailsSection item xs={12} md={2} pr={1}>
                          <Typography variant="body2" color="base.grey4">
                            {t(labelKey)}
                          </Typography>
                        </DraftDetailsSection>
                        <DraftDetailsSection item xs={12} md={10}>
                          <EconsultClinicalReportCategorySection
                            noteContent={content}
                            onContentChanged={onContentChanged}
                            onDelete={() => handleOnCategorySectionDeleteClicked(key)}
                            readOnly={readOnly}
                            isLabResult={isLabResult}
                            isPathologyReport={isPathologyReport}
                            isDiagnosticReport={isDiagnosticReport}
                            isEncounter={isEncounter}
                          />
                        </DraftDetailsSection>
                      </React.Fragment>
                    )
                  )}
                  {isClinicalReportsLoading && (
                    <DraftDetailsSection item xs={12}>
                      <LoadingInProgress />
                    </DraftDetailsSection>
                  )}
                </>
              )}
            </>
          )}
        </Grid>
        {(caseDetails?.programType || caseDetails?.patient) && (
          <>
            <Box mt={3}>
              <EconsultDraftCaseActions
                onSaveDraft={handleDraftSaved}
                onDeleteDraft={handleDraftDeleted}
                onSend={handleDraftSend}
                onAttach={handleFileAttach}
                getSaveDraftDisabled={getSaveDraftDisabled}
                disableActionButtons={disableActionButtons}
              />
            </Box>
            <Box mt={3} ref={attachmentSectionRef}>
              <EconsultDragAndDrop
                ref={attachFileRef}
                onStartFileUpload={handleOnStartAttachment}
                onCompleteFileUpload={handleOnCompleteFileUpload}
                draftUploadedFiles={draftFiles}
                allowDelete={!caseDetails?.originalCaseId}
              />
            </Box>
          </>
        )}
      </Stack>
      <EconsultDeleteDraftDialog
        isOpen={isDeleteDraftDialogOpen}
        onConfirm={handleDraftDeleteConfirm}
        onCancel={() => setIsDeleteDraftDialogOpen(false)}
      />
      <EconsultOutOfOfficeDialog
        isDialogOpen={Boolean(outOfOfficeConsultant)}
        startDate={consultOutOfOfficeDate?.startDate}
        endDate={consultOutOfOfficeDate?.endDate}
        onCancelClicked={handleOutOfOfficeCancel}
        onContinueClicked={handleOutOfOfficeContinue}
        userName={NameUtils.makeName(
          outOfOfficeConsultant?.lastName || '',
          outOfOfficeConsultant?.firstName,
          null,
          null,
          null,
          null,
          null,
          null
        )}></EconsultOutOfOfficeDialog>
      <EconsultPriorityRegionDialog
        isOpen={isPriorityRegionDialogOpen}
        onClose={handleSkipPriorityRegion}
        onComplete={handleSetPriorityRegion}
      />
      <EconsultSelectRequesterDialog
        isDialogOpen={isSelectRequesterDialogOpen}
        options={myConsultants}
        onConfirm={handleDelegatorUserSelected}
        onCancel={handleSelectedRequesterCancel}
        userId={caseDetails?.referrer?.userId?.toString() || '0'}
      />
      <EconsultDraftLostWarningDialog
        isOpen={isDraftWarningDialogOpen}
        onLeave={onDraftWarningDialogCancelled}
        onStay={onDraftWarningDialogConfirmed}
      />
      <EconsultCategoryDeleteConfirmation
        isOpen={isDeleteCategorySectionDialogOpen}
        onCancel={handleDeleteCategorySectionCancel}
        onContinue={handleDeleteCategorySectionConfirmation}
      />
    </>
  );
};

export default EconsultCaseDraftDetailsView;
